/**
 * Constantes y resolución del idioma de la app (i18n ES/EN/PT).
 *
 * Espeja el modelo de pitts-web (locales explícitos, default 'es', preferencia
 * del usuario recordada) pero SIN rutear por URL: en un dashboard autenticado el
 * idioma se cambia en caliente y se persiste en localStorage.
 */

export type AppLang = 'es' | 'en' | 'pt';

export const APP_LANGS: readonly AppLang[] = ['es', 'en', 'pt'] as const;
export const DEFAULT_LANG: AppLang = 'es';
export const LANG_STORAGE_KEY = 'lang';

/**
 * Cookie de idioma compartido con pitts-web (`NEXT_LOCALE`, mismo nombre que usa
 * next-intl). En producción se escribe con `domain=.pitts.app` para que ambas
 * apps (subdominios distintos) lean/escriban el mismo valor: el idioma elegido
 * en la landing pública se propaga solo al entrar al dashboard, y viceversa.
 */
const SHARED_LOCALE_COOKIE = 'NEXT_LOCALE';

function readSharedLocaleCookie(): AppLang | null {
    try {
        const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
        const value = match?.[1];
        return value === 'es' || value === 'en' || value === 'pt' ? value : null;
    } catch {
        return null;
    }
}

/** Persiste el idioma en el cookie compartido con pitts-web. Llamar junto con localStorage. */
export function writeSharedLocaleCookie(lang: AppLang): void {
    try {
        const domain = location.hostname.endsWith('.pitts.app') ? '; domain=.pitts.app' : '';
        // Una cookie vieja solo de este subdominio le ganaría a la compartida
        // al leer (va primero en document.cookie) y el cambio de idioma no se
        // notaría: se borra antes de escribir la nueva.
        document.cookie = `${SHARED_LOCALE_COOKIE}=; path=/; max-age=0; samesite=lax`;
        document.cookie = `${SHARED_LOCALE_COOKIE}=${lang}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax${domain}`;
    } catch {
        /* cookie no disponible: no-op, localStorage sigue siendo la fuente de verdad local */
    }
}

/** Nombres nativos para el switcher. */
export const LANG_LABELS: Record<AppLang, string> = {
    es: 'Español',
    en: 'English',
    pt: 'Português',
};

/**
 * Idioma inicial: cookie compartido con pitts-web → preferencia guardada en
 * esta app → idioma del navegador → 'es'.
 *
 * El cookie va primero porque es la única fuente que se mantiene sincronizada
 * en ambas direcciones (elegir idioma en pitts-web lo actualiza; elegir
 * idioma dentro del dashboard también lo actualiza vía writeSharedLocaleCookie
 * en LanguageService.setLanguage). localStorage solo se usa como respaldo
 * para cuando el navegador bloquea cookies de terceros/cross-subdomain — si
 * localStorage fuera primero, la primera visita al dashboard dejaría un valor
 * ahí que le ganaría para siempre al cookie, aunque el usuario cambie de
 * idioma en pitts-web después.
 *
 * Se llama tanto desde el factory de LOCALE_ID (en bootstrap) como desde el
 * LanguageService, así que debe ser pura y no lanzar.
 */
export function resolveInitialLang(): AppLang {
    try {
        const shared = readSharedLocaleCookie();
        if (shared) return shared;
        const stored = localStorage.getItem(LANG_STORAGE_KEY);
        if (stored === 'es' || stored === 'en' || stored === 'pt') return stored;
        const nav = (typeof navigator !== 'undefined' ? navigator.language : '') || '';
        const navLower = nav.toLowerCase();
        if (navLower.startsWith('en')) return 'en';
        if (navLower.startsWith('pt')) return 'pt';
        return DEFAULT_LANG;
    } catch {
        return DEFAULT_LANG;
    }
}
